const directionDistances = {
  right: ({ distanceX }) => distanceX,
  left: ({ distanceX }) => -distanceX,
  down: ({ distanceY }) => distanceY,
  up: ({ distanceY }) => -distanceY,
};

const horizontalDirections = new Set(['left', 'right']);
const swipeTransforms = {
  right: (distance) => `translate3d(${distance}px, 0, 0)`,
  left: (distance) => `translate3d(${-distance}px, 0, 0)`,
  down: (distance) => `translate3d(0, ${distance}px, 0)`,
  up: (distance) => `translate3d(0, ${-distance}px, 0)`,
};

export default function bindSwipeGesture(
  element,
  {
    direction = 'right',
    threshold = 60,
    axisRatio = 1,
    moveElement = element,
    duration = 200,
    showHandle = true,
    shouldSwipe,
    onSwipe,
  } = {}
) {
  if (!element || !moveElement || typeof onSwipe !== 'function') {
    return () => {};
  }

  const getDirectionDistance = directionDistances[direction];
  const getSwipeTransform = swipeTransforms[direction];
  if (!getDirectionDistance || !getSwipeTransform) {
    return () => {};
  }

  let touchStart = null;
  let gestureAxis = null;
  let suppressNextClick = false;
  let suppressClickTimer = null;
  let transitionTimer = null;
  const originalStyles = {
    transform: moveElement.style.transform,
    transition: moveElement.style.transition,
    willChange: moveElement.style.willChange,
  };
  const previousDirection = moveElement.getAttribute('data-swipe-gesture-direction');

  if (showHandle) {
    moveElement.setAttribute('data-swipe-gesture-direction', direction);
  }

  const restoreStyles = () => {
    moveElement.style.transform = originalStyles.transform;
    moveElement.style.transition = originalStyles.transition;
    moveElement.style.willChange = originalStyles.willChange;
  };

  const animateTo = (distance, callback) => {
    window.clearTimeout(transitionTimer);
    moveElement.style.transition = `transform ${duration}ms ease-out`;
    moveElement.style.transform = getSwipeTransform(distance);
    transitionTimer = window.setTimeout(() => {
      callback?.();
      window.requestAnimationFrame(restoreStyles);
    }, duration);
  };

  const resetTouch = () => {
    touchStart = null;
    gestureAxis = null;
  };

  const handleTouchStart = (event) => {
    if (event.touches.length !== 1) {
      resetTouch();
      return;
    }

    const touch = event.touches[0];
    touchStart = {
      x: touch.clientX,
      y: touch.clientY,
    };
    moveElement.style.willChange = 'transform';
  };

  const handleTouchMove = (event) => {
    const touch = event.touches[0];
    if (!touch || !touchStart) {
      return;
    }

    const distances = {
      distanceX: touch.clientX - touchStart.x,
      distanceY: touch.clientY - touchStart.y,
    };
    const directionDistance = getDirectionDistance(distances);
    const mainAxisDistance = horizontalDirections.has(direction)
      ? Math.abs(distances.distanceX)
      : Math.abs(distances.distanceY);
    const crossAxisDistance = horizontalDirections.has(direction)
      ? Math.abs(distances.distanceY)
      : Math.abs(distances.distanceX);

    if (!gestureAxis && Math.max(mainAxisDistance, crossAxisDistance) >= 8) {
      gestureAxis = mainAxisDistance > crossAxisDistance * axisRatio
        ? 'main'
        : 'cross';
    }

    if (gestureAxis !== 'main' || directionDistance <= 0) {
      return;
    }

    event.preventDefault();
    moveElement.style.transition = 'none';
    moveElement.style.transform = getSwipeTransform(directionDistance);
  };

  const handleTouchEnd = (event) => {
    const touch = event.changedTouches[0];
    if (!touch || !touchStart) {
      resetTouch();
      return;
    }

    const distances = {
      distanceX: touch.clientX - touchStart.x,
      distanceY: touch.clientY - touchStart.y,
    };
    const directionDistance = getDirectionDistance(distances);
    const crossAxisDistance = horizontalDirections.has(direction)
      ? Math.abs(distances.distanceY)
      : Math.abs(distances.distanceX);
    const accepted =
      directionDistance >= threshold &&
      directionDistance > crossAxisDistance * axisRatio &&
      (typeof shouldSwipe !== 'function' ||
        shouldSwipe({ ...distances, direction, event }) !== false);

    resetTouch();

    if (!accepted) {
      animateTo(0);
      return;
    }

    const drawerSize = horizontalDirections.has(direction)
      ? moveElement.getBoundingClientRect().width
      : moveElement.getBoundingClientRect().height;
    animateTo(Math.max(directionDistance, drawerSize), () => {
      onSwipe({ ...distances, direction, event });
    });

    suppressNextClick = true;
    window.clearTimeout(suppressClickTimer);
    suppressClickTimer = window.setTimeout(() => {
      suppressNextClick = false;
    }, 400);
  };

  const handleTouchCancel = () => {
    resetTouch();
    animateTo(0);
  };

  const handleClick = (event) => {
    if (!suppressNextClick) {
      return;
    }

    suppressNextClick = false;
    event.preventDefault();
    event.stopPropagation();
  };

  element.addEventListener('touchstart', handleTouchStart, { passive: true });
  element.addEventListener('touchmove', handleTouchMove, { passive: false });
  element.addEventListener('touchend', handleTouchEnd, { passive: true });
  element.addEventListener('touchcancel', handleTouchCancel, { passive: true });
  element.addEventListener('click', handleClick, true);

  return () => {
    window.clearTimeout(suppressClickTimer);
    window.clearTimeout(transitionTimer);
    element.removeEventListener('touchstart', handleTouchStart);
    element.removeEventListener('touchmove', handleTouchMove);
    element.removeEventListener('touchend', handleTouchEnd);
    element.removeEventListener('touchcancel', handleTouchCancel);
    element.removeEventListener('click', handleClick, true);
    restoreStyles();
    if (showHandle) {
      if (previousDirection === null) {
        moveElement.removeAttribute('data-swipe-gesture-direction');
      } else {
        moveElement.setAttribute('data-swipe-gesture-direction', previousDirection);
      }
    }
  };
}
